import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, theme as antTheme } from 'antd';
import { ParameterConfigProvider } from './context/ParameterContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';
import { useDirection } from './hooks/useDirection';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';

// Pages — lazy loaded
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const NewsletterPage = React.lazy(() => import('./pages/NewsletterPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const BetaLoginPage = React.lazy(() => import('./pages/BetaLoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const UseCasesPage = React.lazy(() => import('./pages/UseCasesPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const ForbiddenPage = React.lazy(() => import('./pages/ForbiddenPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const TermsPage = React.lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/legal/PrivacyPage'));
const NDAPage = React.lazy(() => import('./pages/legal/NDAPage'));

// Admin pages 
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const RoleManagementPage = React.lazy(() => import('./pages/RoleManagementPage'));
const SanctionsManagementPage = React.lazy(() => import('./pages/SanctionsManagementPage'));
const CountryRiskPage = React.lazy(() => import('./pages/CountryRiskPage'));
const ScreeningManagementPage = React.lazy(() => import('./pages/ScreeningManagementPage'));
const RiskReportsPage = React.lazy(() => import('./pages/RiskReportsPage'));
const CustomerClassificationPage = React.lazy(() => import('./pages/CustomerClassificationPage'));
const TransactionMonitoringPage = React.lazy(() => import('./pages/TransactionMonitoringPage'));
const AlertsMonitoringPage = React.lazy(() => import('./pages/AlertsMonitoringPage'));
const TransactionDashboardPage = React.lazy(() => import('./pages/TransactionDashboardPage'));
const AuditLogViewerPage = React.lazy(() => import('./pages/AuditLogViewerPage'));
const PEPManagementPage = React.lazy(() => import('./pages/PEPManagementPage'));
const DueDiligenceDashboardPage = React.lazy(() => import('./pages/DueDiligenceDashboardPage'));
const MonitoringRulesScenariosPage = React.lazy(() => import('./pages/MonitoringRulesScenariosPage'));
const InvestigationsPage = React.lazy(() => import('./pages/InvestigationsPage'));
const IntegrationManagementPage = React.lazy(() => import('./pages/IntegrationManagementPage'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const FATCAManagementPage = React.lazy(() => import('./pages/FATCAManagementPage'));
const UnusualOperationsPage = React.lazy(() => import('./pages/UnusualOperationsPage'));
const SanctionsMatchingPage = React.lazy(() => import('./pages/SanctionsMatchingPage'));
const PendingApprovals = React.lazy(() => import('./pages/PendingApprovals'));
const SystemParametersPage = React.lazy(() => import('./pages/SystemParametersPage'));
const AlertSuppression = React.lazy(() => import('./pages/AlertSuppression'));
const BehavioralProfile = React.lazy(() => import('./pages/BehavioralProfile'));
const AIReportingDashboardPage = React.lazy(() => import('./pages/AIReportingDashboardPage'));
const STRReferralsPage = React.lazy(() => import('./pages/STRReferralsPage'));
const SystemConfigScreen = React.lazy(() => import('./pages/SystemConfigScreen'));
const CustomerRiskProfilePage = React.lazy(() => import('./pages/CustomerRiskProfilePage'));
const KPIDashboardPage = React.lazy(() => import('./pages/KPIDashboardPage'));

// Auth Security Pages (Prompt #52)
const ForcePasswordChangePage = React.lazy(() => import('./pages/ForcePasswordChangePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ChangePasswordPage = React.lazy(() => import('./pages/ChangePasswordPage'));

function AppContent() {
  const direction = useDirection();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <ConfigProvider
      direction={direction}
      theme={{
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#0052FF',
          ...(isDark ? {
            colorBgContainer: '#151B2B',
            colorBgLayout: '#0A0F1C',
            colorBgElevated: '#151B2B',
            colorText: '#F1F5F9',
            colorTextSecondary: '#8B9BB4',
            colorTextTertiary: '#64748B',
            colorBorder: '#2E3A59',
            colorBorderSecondary: '#151B2B',
          } : {}),
          borderRadius: 6,
          fontFamily: `'Tajawal', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        },
        components: isDark ? {
          Table: {
            colorBgContainer: '#151B2B',
            headerBg: '#151B2B',
            rowHoverBg: 'rgba(0, 82, 255, 0.06)',
            borderColor: '#2E3A59',
          },
          Card: {
            colorBgContainer: '#151B2B',
          },
          Modal: {
            colorBgElevated: '#151B2B',
          },
          Drawer: {
            colorBgElevated: '#151B2B',
          },
          Input: {
            colorBgContainer: '#0D1322',
          },
          Select: {
            colorBgContainer: '#0D1322',
          },
          DatePicker: {
            colorBgContainer: '#0D1322',
          },
          Tag: {
            defaultBg: 'rgba(255,255,255,0.06)',
          },
        } : {},
      }}
    >
      <React.Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              background: '#0A0F1C',
              color: '#0052FF',
              fontSize: '18px',
            }}
          >
            Loading...
          </div>
        }
      >
        <Router>
          <Routes>
            {/* Public pages — no layout wrapper */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/beta" element={<BetaLoginPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/use-cases" element={<UseCasesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />

            {/* Dashboard — standalone page with its own layout */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Legal */}
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/nda" element={<NDAPage />} />

            {/* Auth Security (Prompt #52) */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/change-password/first-login" element={<ForcePasswordChangePage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />

            {/* ── Authenticated pages inside persistent MainLayout ── */}
            <Route element={<MainLayout />}>
              {/* Reports */}
              <Route path="/reports" element={<ReportsPage />} />

              {/* User & Role Management */}
              <Route path="/user-management" element={<UserManagementPage />} />
              <Route path="/role-management" element={<RoleManagementPage />} />

              {/* Admin */}
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/sanctions"
                element={
                  <ProtectedRoute action="sanctions:read">
                    <SanctionsManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/country-risk" element={<CountryRiskPage />} />
              <Route path="/admin/screenings" element={<ScreeningManagementPage />} />
              <Route path="/admin/reports" element={<RiskReportsPage />} />
              <Route path="/admin/customers" element={<CustomerClassificationPage />} />
              <Route path="/admin/customers/:id/risk-profile" element={<CustomerRiskProfilePage />} />
              <Route path="/admin/transactions" element={<TransactionMonitoringPage />} />
              <Route path="/admin/AlertsMonitoring" element={<AlertsMonitoringPage />} />
              <Route path="/admin/transaction-dashboard" element={<TransactionDashboardPage />} />
              <Route path="/admin/system-config" element={<SystemConfigScreen />} />
              <Route path="/admin/audit-logs" element={<AuditLogViewerPage />} />
              <Route path="/admin/pep" element={<PEPManagementPage />} />
              <Route path="/admin/due-diligence" element={<DueDiligenceDashboardPage />} />
              <Route path="/admin/monitoring-rules-scenarios" element={<MonitoringRulesScenariosPage />} />
              <Route path="/admin/investigations" element={<InvestigationsPage />} />
              <Route path="/admin/integrations" element={<IntegrationManagementPage />} />
              <Route path="/admin/onboarding" element={<OnboardingPage />} />
              <Route path="/admin/fatca" element={<FATCAManagementPage />} />
              <Route path="/admin/unusual-operations" element={<UnusualOperationsPage />} />
              <Route path="/admin/sanctions-matching" element={<SanctionsMatchingPage />} />
              <Route
                path="/admin/approvals"
                element={
                  <ProtectedRoute action="approvals:read">
                    <PendingApprovals />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/parameters" element={<SystemParametersPage />} />
              <Route path="/admin/alert-suppression" element={<AlertSuppression />} />
              <Route path="/admin/behavioral-profile/:customerId" element={<BehavioralProfile />} />
              <Route
                path="/admin/ai-reporting"
                element={
                  <ProtectedRoute>
                    <AIReportingDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/str-referrals"
                element={
                  <ProtectedRoute action="str:read">
                    <STRReferralsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/kpi-dashboard"
                element={
                  <ProtectedRoute action="kpi:read">
                    <KPIDashboardPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Router>
      </React.Suspense>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ParameterConfigProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ParameterConfigProvider>
    </ThemeProvider>
  );
}

export default App;
